/* eslint-disable max-lines */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Descendant, Editor, Node, Text, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import Peaks from 'peaks.js';
import {
  transcriptDocumentsEqual,
  transcriptParagraphsEqual
} from '../editors/transcript-content-equality.ts';
import { Session } from '@utils/types/editors/sessions.ts';
import {
  DocumentObjectInnerDocument,
  DocumentResult
} from '@utils/types/editors/generic.ts';
import { StartStopWord } from '@utils/types/editors/utils.ts';
import {
  DocumentTranscriptParagraphV1,
  DocumentTranscriptTextV1,
  DocumentTranscriptUtteranceV1,
  DocumentTranscriptV1,
  ScriptixDescendant
} from '@utils/types/editors/transcripts/transcript.ts';
import { Comment } from '@utils/types/comments/comments.ts';
import { VoiceTimelineSpan } from '@utils/types/organization/voice-profile.ts';
import { applyVoiceNameToUtterances } from '@utils/editors/apply-voice-name.ts';
import { useUpdateDocument } from '@utils/api/documents/use-update-document.tsx';
import { useResetDocument } from '@utils/api/documents/use-reset-document.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useToggleFinishedStatus } from '@utils/api/magic-links/toggle-finalize-document.tsx';
import { useUnsavedChanges } from '@utils/providers/unsaved-changes-context.tsx';
import {
  clearTranscriptFromLocalStorage,
  saveTranscriptToLocalStorage,
  loadTranscriptFromLocalStorage
} from '@utils/localStorage/transcript-storage.ts';
import { resolveDraftRecovery } from '@utils/localStorage/recovery-decision.ts';
import {
  lastModifiedFromUpdateResponse,
  probeServerLastModified,
  setBaseline,
  syncKey
} from '@utils/editors/document-sync-baseline.ts';
import {
  DraftConflictData,
  forceCloudSourceRequested
} from '@utils/localStorage/draft-conflict.ts';
import type { TranscriptContextType, State } from './transcript-context-state';
import {
  initialState,
  reducer,
  normalizeSlateDocument
} from './transcript-context-state';

export function useTranscriptContextLogic({
  session,
  document: transcriptDocument
}: {
  session?: Session;
  document?: DocumentResult;
}): {
  contextValue: TranscriptContextType;
  conflict: DraftConflictData<DocumentTranscriptV1> | null;
  resolveConflictKeepLocal: () => void;
  resolveConflictLoadCloud: () => void;
} {
  const { t } = useTranslation();
  const { setHasUnsavedChanges } = useUnsavedChanges();

  // Normalize document to ensure all Slate nodes have children arrays
  const normalizedDocument = normalizeSlateDocument(
    transcriptDocument?.result.document as DocumentTranscriptV1
  );

  // Store initial state in a ref for recovery logic
  const initialStateRef = useRef<State>({
    ...initialState,
    session,
    document: normalizedDocument,
    documentResult: transcriptDocument
  });
  // Holds the unresolved local-vs-cloud conflict, if any (see DraftConflictModal).
  const initialConflictRef =
    useRef<DraftConflictData<DocumentTranscriptV1> | null>(null);

  // Recovery logic - run once on mount
  if (
    !initialStateRef.current.undoStack ||
    initialStateRef.current.undoStack.length === 0
  ) {
    let recoveredData: {
      document: DocumentTranscriptV1;
      undoStack: DocumentTranscriptV1[];
      redoStack: DocumentTranscriptV1[];
      isDirty: boolean;
      recoveryTimestamp: number;
    } | null = null;

    // The "view cloud version" tab bypasses local recovery entirely.
    const forceCloud = forceCloudSourceRequested();

    // Try to recover from localStorage if available
    if (session && transcriptDocument && !forceCloud) {
      try {
        const localData = loadTranscriptFromLocalStorage(
          session.session_id,
          transcriptDocument.result.id
        );

        if (localData) {
          const serverTimestamp = new Date(
            transcriptDocument.result.last_modified
          ).getTime();
          const localTimestamp = localData.lastLocalSave;

          // Compare local document with server document to determine if actually dirty.
          // Normalize the server document first so that structural differences introduced
          // by normalizeSlateDocument (e.g. adding missing children arrays) are not
          // counted as real user edits.
          const serverDoc = transcriptDocument.result
            .document as DocumentTranscriptV1;
          const normalizedServerDoc =
            normalizeSlateDocument(serverDoc) ?? serverDoc;
          const documentsDiffer = !transcriptDocumentsEqual(
            localData.document,
            normalizedServerDoc
          );

          // Reconcile the local draft with the server copy:
          //  - 'recover'  → genuine unsynced edits, server not ahead: load local.
          //  - 'server'   → local is stale/clean: server is authoritative.
          //  - 'conflict' → both advanced independently: ask the user (block the
          //    editor behind DraftConflictModal). Never silently drop either side
          //    (data-loss guard, Smartcomtv incident 2026-06).
          const decision = resolveDraftRecovery({
            localIsDirty: localData.isDirty,
            localLastSave: localTimestamp,
            localLastServerSync: localData.lastServerSync,
            serverLastModified: serverTimestamp,
            documentsDiffer
          });
          if (decision === 'recover') {
            const isActuallyDirty = documentsDiffer;

            recoveredData = {
              document: localData.document,
              undoStack: localData.undoStack || [],
              redoStack: localData.redoStack || [],
              isDirty: isActuallyDirty,
              recoveryTimestamp: localTimestamp || Date.now()
            };
          } else if (decision === 'conflict') {
            // Keep the server copy as the safe live baseline; capture the local
            // draft so the modal can offer it. Nothing is applied until the user
            // chooses, so no autosave can clobber either version.
            initialConflictRef.current = {
              localDocument: localData.document,
              localUndo: localData.undoStack || [],
              localRedo: localData.redoStack || [],
              localTimestamp: localTimestamp || 0,
              serverTimestamp,
              localCount: localData.document.document?.length,
              serverCount: normalizedServerDoc?.document?.length
            };
          }
        }
      } catch (error) {
        // Failed to load from localStorage
      }
    }

    let saveStatus: 'unsaved' | 'saved-locally' | 'synced' = 'synced';
    if (recoveredData) {
      // If we recovered from localStorage, we have unsaved changes
      saveStatus = recoveredData.recoveryTimestamp
        ? 'saved-locally'
        : 'unsaved';
    }

    initialStateRef.current = {
      ...initialStateRef.current,
      document: recoveredData?.document ?? initialStateRef.current.document,
      undoStack: recoveredData?.undoStack ?? [],
      redoStack: recoveredData?.redoStack ?? [],
      isDirty: recoveredData?.isDirty ?? false,
      saveStatus,
      lastLocalSave: recoveredData?.recoveryTimestamp ?? null
    };
  }

  const [state, dispatch] = useReducer(reducer, initialStateRef.current);

  // Unresolved local-vs-cloud conflict. While set, the editor is blocked behind
  // DraftConflictModal so neither version can be silently overwritten.
  const [conflict, setConflict] =
    useState<DraftConflictData<DocumentTranscriptV1> | null>(
      () => initialConflictRef.current
    );

  // Keep session in sync when the prop changes (e.g. after media_source update)
  useEffect(() => {
    if (session && session !== state.session) {
      dispatch({ type: 'SET_SESSION', payload: session });
    }
  }, [session]);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const { mutate: updateDocumentMutation, isLoading: saveDocumentLoading } =
    useUpdateDocument();
  const { mutate: resetDocument, isLoading: documentResetting } =
    useResetDocument();
  const {
    ref: mediaRef,
    initializePeaks: initializePeaksInAudio,
    setTime
  } = useAudio();
  const currentEditorRef = useRef<ReactEditor | null>(null);
  const { mutate: toggleStatus, isLoading: finishedLoading } =
    useToggleFinishedStatus();

  // Guard against concurrent saves
  const isSavingRef = useRef(false);

  // Undo/redo and localStorage management
  const undoStackRef = useRef<DocumentTranscriptV1[]>(
    initialStateRef.current.undoStack
  );
  const redoStackRef = useRef<DocumentTranscriptV1[]>(
    initialStateRef.current.redoStack
  );
  const documentRef = useRef<DocumentTranscriptV1 | undefined>(
    initialStateRef.current.document
  );

  // Reduced from 200 to 50 to prevent memory leaks (see docs/MEMORY_OPTIMIZATION.md)
  // Each undo entry stores a full document copy (~2MB for large docs)
  // 50 entries is still generous (most users need <20) and reduces memory by 75%
  const MAX_HISTORY = 50;

  // localStorage debounce timer
  const localStorageSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const LOCALSTORAGE_DEBOUNCE_MS = 1000; // 1 second

  // Helper functions for save status
  const setSaveStatus = useCallback(
    (status: 'unsaved' | 'saved-locally' | 'synced' | 'saving') => {
      dispatch({ type: 'SET_SAVE_STATUS', payload: status });
    },
    []
  );

  const setLastLocalSave = useCallback((timestamp: number | null) => {
    dispatch({ type: 'SET_LAST_LOCAL_SAVE', payload: timestamp });
  }, []);

  const setLastServerSync = useCallback((timestamp: number | null) => {
    dispatch({ type: 'SET_LAST_SERVER_SYNC', payload: timestamp });
  }, []);

  // Debounced localStorage save
  const debouncedLocalStorageSave = useCallback(() => {
    if (!state.session || !state.documentResult || !state.document) return;

    if (localStorageSaveTimer.current) {
      clearTimeout(localStorageSaveTimer.current);
    }

    localStorageSaveTimer.current = setTimeout(() => {
      saveTranscriptToLocalStorage(
        state.session!.session_id,
        state.documentResult!.result.id,
        {
          document: state.document!,
          undoStack: undoStackRef.current,
          redoStack: redoStackRef.current,
          isDirty: true, // Always true for debounced saves
          filename: state.documentResult!.result.filename,
          timecodeOffset: state.documentResult!.result.timecode_offset
        }
      );

      setSaveStatus('saved-locally');
      setLastLocalSave(Date.now());
    }, LOCALSTORAGE_DEBOUNCE_MS);
  }, [
    state.session,
    state.documentResult,
    state.document,
    setSaveStatus,
    setLastLocalSave
  ]);

  // Sync refs with state
  useEffect(() => {
    undoStackRef.current = state.undoStack;
    redoStackRef.current = state.redoStack;
  }, [state.undoStack, state.redoStack]);

  // Track if this is the initial mount to prevent overwriting recovered document
  const isInitialMount = useRef(true);

  useEffect(() => {
    documentRef.current = state.document;

    // Don't trigger save on initial mount if we recovered from localStorage
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Trigger debounced localStorage save when document changes
    if (state.document && state.isDirty) {
      debouncedLocalStorageSave();
    }
  }, [state.document, state.isDirty, debouncedLocalStorageSave]);

  useEffect(() => {
    setHasUnsavedChanges(state.isDirty ?? false);

    return () => {
      // Clean up when unmounting
      setHasUnsavedChanges(false);
    };
  }, [state.isDirty, setHasUnsavedChanges]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (localStorageSaveTimer.current) {
        clearTimeout(localStorageSaveTimer.current);
      }
      if (pendingEditTimerRef.current) {
        clearTimeout(pendingEditTimerRef.current);
      }
    };
  }, []);

  const reset = () => {
    if (!state.session || !state.documentResult) return;

    toast(t('resetting-document'));
    resetDocument(
      {
        sessionId: state.session.session_id,
        documentId: state.documentResult.result.id
      },
      {
        onSuccess: freshDocument => {
          toast.success(t('document-has-been-reset-successfully'));

          // Clear localStorage to prevent recovery of old data
          clearTranscriptFromLocalStorage(
            state.session!.session_id,
            state.documentResult!.result.id
          );

          // Update local state with fresh document
          const newDocument = freshDocument.result
            .document as DocumentTranscriptV1;
          dispatch({ type: 'SET_DOCUMENT', payload: newDocument });
          dispatch({ type: 'SET_DOCUMENT_RESULT', payload: freshDocument });
          documentRef.current = newDocument;

          // Clear undo/redo stacks
          dispatch({ type: 'SET_UNDO_STACK', payload: [] });
          dispatch({ type: 'SET_REDO_STACK', payload: [] });
          undoStackRef.current = [];
          redoStackRef.current = [];

          // Force editor to re-render with new document
          dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });

          // Reset dirty state
          dispatch({ type: 'SET_IS_DIRTY', payload: false });
          setSaveStatus('synced');
          setLastServerSync(Date.now());

          // Update speakers list from the new document
          const speakers = Array.from(
            new Set(
              newDocument.document
                .filter(p => p.speaker && p.speaker.length > 0)
                .map(p => p.speaker || '')
            )
          );
          dispatch({ type: 'SET_SPEAKERS', payload: speakers });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const data = error?.response?.data;
          const message =
            data?.error_description ||
            data?.detail ||
            data?.error ||
            data?.message ||
            t('failed-to-reset-document');
          toast.error(message);
        }
      }
    );
  };

  const setSearch = (query: string) => {
    dispatch({ type: 'SET_SEARCH', payload: query });
  };

  const setStartStopWord = (startStopWord: StartStopWord) => {
    dispatch({ type: 'SET_START_STOP_WORD', payload: startStopWord });
  };

  const setPopoverOpen = (open: boolean) => {
    dispatch({ type: 'SET_POPOVER_OPEN', payload: open });
  };

  const setPopoverPosition = (position: { x: number; y: number }) => {
    dispatch({ type: 'SET_POPOVER_POSITION', payload: position });
  };

  const setShowInfo = (showInfo: boolean) => {
    dispatch({ type: 'SET_SHOW_INFO', payload: showInfo });
  };

  const setClickedComment = (comment: Comment | null) => {
    dispatch({ type: 'SET_CLICKED_COMMENT', payload: comment });
  };

  const setShowJumpButton = (direction: 'up' | 'down' | false) => {
    dispatch({ type: 'SET_JUMP_BUTTON', payload: direction });
  };

  /**
   * Record the server version produced by this tab's own save.
   *
   * The concurrent-edit poller compares the server against the version *this
   * tab* is based on rather than against the editing user's id, because a second
   * tab on the same account is indistinguishable by id (issue #2347). That
   * comparison only works if our own writes advance the baseline, which is what
   * this does — from the `last_modified` echoed by the PUT when available, and
   * otherwise from one lightweight probe.
   */
  const recordOwnSave = async (
    sessionId: string,
    documentId: string,
    response: unknown
  ) => {
    if (!sessionId || !documentId) return;
    const key = syncKey(sessionId, documentId);
    const fromResponse = lastModifiedFromUpdateResponse(response);
    if (fromResponse > 0) {
      setBaseline(key, fromResponse);
      return;
    }
    const probed = await probeServerLastModified(sessionId, documentId);
    if (probed != null) setBaseline(key, probed);
  };

  const saveDocument = (userAction?: boolean) => {
    // Use documentRef to get the absolute latest document, avoiding stale state issues
    const currentDocument = documentRef.current || state.document;

    if (!currentDocument || !state.session || !state.documentResult) {
      if (userAction) {
        toast.error(t('unable-to-save-document'));
      }
      return;
    }

    // Don't save if document is finished (locked)
    if (state.documentResult.result.finished) {
      if (userAction) {
        toast.error(t('document-finished'));
      }
      return;
    }

    isSavingRef.current = true;
    setSaveStatus('saving');
    if (userAction) {
      toast(t('saving-document'));
    }

    updateDocumentMutation(
      {
        sessionId: state.session?.session_id as string,
        documentId: state.documentResult?.result.id as string,
        document: currentDocument as DocumentObjectInnerDocument,
        plain_document_changed: !!userAction,
        // Skip query invalidation — we already have the latest document locally.
        // Without this, the mutation's global onSuccess refetches the entire document
        // from Azure Blob after every manual save, which is slow for large documents
        // and can make it appear as if the save didn't complete.
        dontInvalidateQueries: true
      },
      {
        onSuccess: response => {
          isSavingRef.current = false;
          const now = Date.now();
          void recordOwnSave(
            state.session?.session_id as string,
            state.documentResult?.result.id as string,
            response
          );
          setIsDirty(false);
          setSaveStatus('synced');
          setLastServerSync(now);

          // Cancel any pending debounced localStorage save to prevent it from
          // overwriting isDirty: false with isDirty: true after server sync
          if (localStorageSaveTimer.current) {
            clearTimeout(localStorageSaveTimer.current);
            localStorageSaveTimer.current = null;
          }

          // Clear pending edit timer to prevent race condition
          // where debounced addVersionChange fires after save completes
          if (pendingEditTimerRef.current) {
            clearTimeout(pendingEditTimerRef.current);
            pendingEditTimerRef.current = null;
          }

          if (userAction) {
            toast.success(t('document-saved-successfully'));
          }

          // Update localStorage with synced status and current undo/redo stacks
          // Keep existing lastLocalSave - only update lastServerSync
          if (state.session && state.documentResult && currentDocument) {
            saveTranscriptToLocalStorage(
              state.session.session_id,
              state.documentResult.result.id,
              {
                document: currentDocument,
                undoStack: undoStackRef.current,
                redoStack: redoStackRef.current,
                isDirty: false,
                lastLocalSave: state.lastLocalSave ?? undefined, // Convert null to undefined
                lastServerSync: now,
                filename: state.documentResult.result.filename,
                timecodeOffset: state.documentResult.result.timecode_offset
              }
            );
          }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          isSavingRef.current = false;
          // Keep saved-locally status if we have a local save, otherwise unsaved
          setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');

          if (userAction) {
            const errorMessage =
              error?.response?.data?.error_description ||
              error?.response?.data?.detail ||
              error?.response?.data?.error ||
              error?.response?.data?.message ||
              t('failed-to-save-document');
            toast.error(errorMessage);
          }
        }
      }
    );
  };

  // Tracks the wave_info_url the peaks instance was last initialized against.
  // Guards both against double-init on repeated onReady calls and against the
  // old waveform sticking around after regenerate-audio-timeline swaps the URL.
  const lastInitWaveInfoUrlRef = useRef<string | null>(null);

  const handleMediaElementReady = (waveInfoUrl?: string) => {
    if (!mediaRef?.current) return;
    const mediaElement = mediaRef.current.getInternalPlayer();
    if (!mediaElement) return;
    mediaElement.id = 'media-element';
    if (!waveInfoUrl) return;
    if (lastInitWaveInfoUrlRef.current === waveInfoUrl) return;
    lastInitWaveInfoUrlRef.current = waveInfoUrl;
    initializePeaks(waveInfoUrl);
  };

  const initializePeaks = (url: string) => {
    const containerElement = document.getElementById(
      'peaks-overview-container'
    );
    const mediaElement = document.getElementById('media-element');
    if (!containerElement || !mediaElement) return;

    // Ensure container has valid dimensions before initializing peaks.js
    const { clientWidth, clientHeight } = containerElement;
    if (clientWidth <= 0 || clientHeight <= 0) return;

    Peaks.init(
      {
        overview: {
          container: containerElement
        },
        mediaElement: mediaElement,
        dataUri: {
          arraybuffer: url
        },
        keyboard: false,
        // eslint-disable-next-line no-restricted-syntax
        pointMarkerColor: '#0a8df8',
        // eslint-disable-next-line no-restricted-syntax
        playheadColor: '#0a8df8',
        // eslint-disable-next-line no-restricted-syntax
        playheadTextColor: '#0a8df8',
        showPlayheadTime: true,
        waveformCache: true,
        zoomLevels: [512, 1024, 2048, 4096]
      },
      (err, peaks) => {
        if (err) {
          return;
        }
        if (!peaks) return;

        peaks.on('overview.click', function (event) {
          setTime(event.time, 'peaks');
        });

        initializePeaksInAudio(peaks);
      }
    );
  };

  const applyStyle = (
    style:
      | 'bold'
      | 'italic'
      | 'underlined'
      | 'color'
      | 'strike'
      | 'mark'
      | 'unmark',
    colorValue?: string | null,
    editor?: ReactEditor
  ) => {
    if (!editor && currentEditorRef.current) {
      editor = currentEditorRef.current;
    }
    if (!editor || !state.startStopWord) return;

    // Add to undo stack before making changes
    addVersionChange();

    const { startStopWord } = state;

    Editor.withoutNormalizing(editor, () => {
      for (const [node, path] of Editor.nodes(editor!, {
        at: [],
        match: (n, nodePath) => {
          if (!Text.isText(n)) return false;
          const t = n as DocumentTranscriptTextV1;
          if (typeof t.start !== 'number' || typeof t.stop !== 'number')
            return false;
          if (
            !(
              (t.start >= startStopWord!.start || t.start === 0) &&
              t.stop <= startStopWord!.stop
            )
          )
            return false;
          // Skip text nodes inside speaker-type utterances
          if (nodePath.length > 0) {
            const utterance = Node.get(editor!, [nodePath[0]]) as {
              type?: string;
            };
            if (utterance.type === 'speaker') return false;
          }
          return true;
        }
      })) {
        const textNode = node as DocumentTranscriptTextV1;
        const props: Partial<DocumentTranscriptTextV1> = {};
        if (style === 'unmark') {
          props.mark = false;
        } else if (style === 'mark') {
          props.mark = colorValue ? true : !textNode.mark;
          if (colorValue) props.color = colorValue;
        } else if (style === 'color' && colorValue) {
          props.color = colorValue;
        } else {
          (props as Record<string, boolean>)[style] =
            !textNode[style as keyof DocumentTranscriptTextV1];
        }
        Transforms.setNodes(editor!, props as Partial<Descendant>, {
          at: path
        });
      }
    });

    setIsDirty(true);
  };

  const setCaseSensitive = (caseSensitive: boolean) => {
    dispatch({ type: 'SET_CASE_SENSITIVE', payload: caseSensitive });
  };

  const setWholeWord = (wholeWord: boolean) => {
    dispatch({ type: 'SET_WHOLE_WORD', payload: wholeWord });
  };

  const setCurrentMatch = (match: DocumentTranscriptTextV1 | null) => {
    dispatch({ type: 'SET_CURRENT_MATCH', payload: match });
  };

  const addUtterance = (isFirstIndex?: boolean) => {
    if (!state.document) return;

    const currentEditor = currentEditorRef.current;
    if (!currentEditor) return;

    const { selection } = currentEditor;
    if (!selection) return;

    const [selectedNode] = Editor.node(currentEditor, selection.focus.path);
    if (
      !selectedNode ||
      !('start' in selectedNode) ||
      !('stop' in selectedNode)
    )
      return;

    // Add to undo stack before making changes
    addVersionChange();

    const updatedDocument = [...state.document.document];

    // If isFirstIndex is true, insert at the beginning
    if (isFirstIndex && updatedDocument.length > 0) {
      const firstUtterance = updatedDocument[0];
      const newUtterance: DocumentTranscriptUtteranceV1 = {
        type: 'utterance',
        speaker: firstUtterance.speaker,
        start: 0,
        stop: firstUtterance.start,
        children: [
          {
            type: 'paragraph',
            start: 0,
            stop: firstUtterance.start,
            children: [
              {
                text: ' ',
                start: 0,
                stop: firstUtterance.start
              }
            ]
          }
        ]
      };

      updatedDocument.splice(0, 0, newUtterance);

      const newDoc = {
        ...state.document,
        document: updatedDocument
      };

      // Update ref immediately to avoid race conditions
      documentRef.current = newDoc;

      dispatch({
        type: 'SET_DOCUMENT',
        payload: newDoc
      });

      // Broadcast that the newly added row (index 0) is now active
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('transcript:activeRow', { detail: { index: 0 } })
        );
      }, 50);

      setTimeout(() => {
        try {
          ReactEditor.blur(currentEditor);
        } catch {
          // Silently handle Slate DOM resolution errors
        }
      }, 100);
      setIsDirty(true);
      return;
    }

    const currentUtteranceIndex = updatedDocument.findIndex(
      utterance =>
        (utterance.start || 0) <= Number(selectedNode.start) &&
        (utterance.stop || 0) >= Number(selectedNode.stop)
    );

    if (currentUtteranceIndex !== -1) {
      const currentUtterance = updatedDocument[currentUtteranceIndex];
      const nextUtterance = updatedDocument[currentUtteranceIndex + 1];

      const newStart = Number(selectedNode.stop);
      const newStop = nextUtterance?.start ?? Number(selectedNode.stop);

      const newUtterance: DocumentTranscriptUtteranceV1 = {
        type: 'utterance',
        speaker: currentUtterance.speaker,
        start: newStart,
        stop: newStop,
        children: [
          {
            type: 'paragraph',
            start: newStart,
            stop: newStop,
            children: [
              {
                text: ' ',
                start: newStart,
                stop: newStop
              }
            ]
          }
        ]
      };

      updatedDocument.splice(currentUtteranceIndex + 1, 0, newUtterance);

      const newDoc = {
        ...state.document,
        document: updatedDocument
      };

      // Update ref immediately to avoid race conditions
      documentRef.current = newDoc;

      dispatch({
        type: 'SET_DOCUMENT',
        payload: newDoc
      });

      // Broadcast that the newly added row is now active
      const newRowIndex = currentUtteranceIndex + 1;
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('transcript:activeRow', {
            detail: { index: newRowIndex }
          })
        );
      }, 50);

      setTimeout(() => {
        try {
          ReactEditor.blur(currentEditor);
        } catch {
          // Silently handle Slate DOM resolution errors
        }
      }, 100);
      setIsDirty(true);
    }
  };

  const splitUtterance = (index: number) => {
    if (!state || !state.document) return;

    const currentEditor = currentEditorRef.current;
    if (!currentEditor) return;

    const { selection } = currentEditor;
    if (!selection) return;
    const savedSelection = { ...selection };

    const [selectedNode] = Editor.node(currentEditor, selection.focus.path);
    if (
      !selectedNode ||
      !('start' in selectedNode) ||
      !('stop' in selectedNode)
    )
      return;

    // Add to undo stack before making changes
    addVersionChange();

    const splitStartTime = Number(selectedNode.start);
    const updatedDocument = [...state.document.document];
    const utteranceToSplit = updatedDocument[index];

    const beforeChildren = utteranceToSplit.children
      .map(child => ({
        ...child,
        children: child.children.filter(
          textNode =>
            textNode.start !== undefined &&
            textNode.stop !== undefined &&
            textNode.stop <= splitStartTime
        ),
        start: child.children[0]?.start,
        stop: splitStartTime
      }))
      .filter(child => child.children.length > 0);

    const afterChildren = utteranceToSplit.children
      .map(child => ({
        ...child,
        children: child.children.filter(
          textNode =>
            textNode.start !== undefined &&
            textNode.stop !== undefined &&
            textNode.start >= splitStartTime
        ),
        start: splitStartTime,
        stop: child.children[child.children.length - 1]?.stop
      }))
      .filter(child => child.children.length > 0);

    const beforeSplit = {
      ...utteranceToSplit,
      start: utteranceToSplit.start,
      stop: splitStartTime,
      children: beforeChildren
    };

    const afterSplit = {
      ...utteranceToSplit,
      start: splitStartTime,
      stop: utteranceToSplit.stop,
      children: afterChildren,
      speaker: utteranceToSplit.speaker
    };

    if (beforeChildren.length > 0 && afterChildren.length > 0) {
      updatedDocument.splice(index, 1, beforeSplit, afterSplit);

      const newDoc = {
        ...state.document,
        document: updatedDocument
      };

      // Update ref immediately to avoid race conditions
      documentRef.current = newDoc;

      dispatch({
        type: 'SET_DOCUMENT',
        payload: newDoc
      });

      setTimeout(() => {
        try {
          currentEditor.selection = savedSelection;
          ReactEditor.focus(currentEditor);
        } catch {
          // Silently handle Slate DOM resolution errors
        }
      }, 100);
      setIsDirty(true);
    }
  };

  const deleteUtterance = (index: number) => {
    if (!state.document) return;

    // Add to undo stack before making changes
    addVersionChange();

    const updatedDocument = [...state.document.document];

    // Remove the utterance at the given index
    updatedDocument.splice(index, 1);

    const newDoc = {
      ...state.document,
      document: updatedDocument
    };

    // Update ref immediately to avoid race conditions
    documentRef.current = newDoc;

    dispatch({
      type: 'SET_DOCUMENT',
      payload: newDoc
    });
    setIsDirty(true);
  };

  const setEditor = (editor: ReactEditor | null) => {
    currentEditorRef.current = editor;
  };

  const updateDocument = (document: DocumentTranscriptV1) => {
    dispatch({ type: 'SET_DOCUMENT', payload: document });
  };

  const updateDocumentResult = (documentResult: DocumentResult) => {
    dispatch({ type: 'SET_DOCUMENT_RESULT', payload: documentResult });
  };

  const reloadFromServer = (freshDocument: DocumentResult) => {
    const newDocument = freshDocument.result.document as DocumentTranscriptV1;

    // Clear localStorage to prevent recovery of old data
    if (state.session && state.documentResult) {
      clearTranscriptFromLocalStorage(
        state.session.session_id,
        state.documentResult.result.id
      );
    }

    // Update local state with fresh document
    dispatch({ type: 'SET_DOCUMENT', payload: newDocument });
    dispatch({ type: 'SET_DOCUMENT_RESULT', payload: freshDocument });
    documentRef.current = newDocument;

    // Clear undo/redo stacks
    dispatch({ type: 'SET_UNDO_STACK', payload: [] });
    dispatch({ type: 'SET_REDO_STACK', payload: [] });
    undoStackRef.current = [];
    redoStackRef.current = [];

    // Force editor to re-render with new document
    dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });

    // Reset dirty state
    dispatch({ type: 'SET_IS_DIRTY', payload: false });
    setSaveStatus('synced');
    setLastServerSync(Date.now());

    // Update speakers list from the new document
    if (newDocument?.document) {
      const speakers = Array.from(
        new Set(
          newDocument.document
            .filter(p => p.speaker && p.speaker.length > 0)
            .map(p => p.speaker || '')
        )
      );
      dispatch({ type: 'SET_SPEAKERS', payload: speakers });
    }
  };

  const silentSave = (updatedDocument?: DocumentTranscriptV1) => {
    // Don't save if there are no unsaved changes
    if (!state.isDirty && !updatedDocument) {
      return;
    }

    if (isSavingRef.current) return;

    if (!state.documentResult || !state.session) return;

    // Don't attempt auto-save if document is finished (locked)
    // This prevents repeated 400 errors from the server
    // User can still unlock the document and save manually
    if (state.documentResult.result.finished) {
      return;
    }

    // Use the latest document from ref to avoid stale closures
    const docToSave = updatedDocument || documentRef.current || state.document;

    if (!docToSave) return;

    isSavingRef.current = true;
    setSaveStatus('saving');

    updateDocumentMutation(
      {
        sessionId: state.session.session_id,
        documentId: state.documentResult!.result.id!,
        document: docToSave as DocumentObjectInnerDocument,
        dontInvalidateQueries: true
      },
      {
        onSuccess: response => {
          isSavingRef.current = false;
          const now = Date.now();
          void recordOwnSave(
            state.session?.session_id as string,
            state.documentResult?.result.id as string,
            response
          );
          setIsDirty(false);
          setSaveStatus('synced');
          setLastServerSync(now);

          // Cancel any pending debounced localStorage save
          if (localStorageSaveTimer.current) {
            clearTimeout(localStorageSaveTimer.current);
            localStorageSaveTimer.current = null;
          }

          // Clear pending edit timer to prevent race condition
          // where debounced addVersionChange fires after save completes
          if (pendingEditTimerRef.current) {
            clearTimeout(pendingEditTimerRef.current);
            pendingEditTimerRef.current = null;
          }

          // Update localStorage with synced status and current undo/redo stacks
          // Keep existing lastLocalSave - only update lastServerSync
          if (state.session && state.documentResult && docToSave) {
            saveTranscriptToLocalStorage(
              state.session.session_id,
              state.documentResult.result.id,
              {
                document: docToSave,
                undoStack: undoStackRef.current,
                redoStack: redoStackRef.current,
                isDirty: false,
                lastLocalSave: state.lastLocalSave ?? undefined, // Convert null to undefined
                lastServerSync: now,
                filename: state.documentResult.result.filename,
                timecodeOffset: state.documentResult.result.timecode_offset
              }
            );
          }
        },
        onError: () => {
          isSavingRef.current = false;
          // On error, keep the unsaved or saved-locally status
          // Don't set to 'unsaved' if we already have it saved locally
          if (state.saveStatus === 'saving') {
            setSaveStatus(state.lastLocalSave ? 'saved-locally' : 'unsaved');
          }
        }
      }
    );
  };

  // Add current document to undo stack before making changes
  const addVersionChange = useCallback(() => {
    if (!state.document) return;

    const nextUndo = [...state.undoStack, state.document];
    // cap history size
    const trimmedUndo =
      nextUndo.length > MAX_HISTORY
        ? nextUndo.slice(nextUndo.length - MAX_HISTORY)
        : nextUndo;

    dispatch({
      type: 'SET_UNDO_STACK',
      payload: trimmedUndo
    });
    dispatch({
      type: 'SET_REDO_STACK',
      payload: []
    });
    dispatch({
      type: 'SET_IS_DIRTY',
      payload: true
    });

    // Only set to 'unsaved' if we're not currently saving
    // This prevents status flicker during save operations
    if (state.saveStatus !== 'saving') {
      setSaveStatus('unsaved');
    }
  }, [state.document, state.undoStack, state.saveStatus]);

  const undo = useCallback(() => {
    if (state.undoStack.length === 0) return;

    const undoStackCopy = [...state.undoStack];
    const lastState = undoStackCopy.pop();

    if (lastState) {
      const newRedoStack = state.document
        ? [...state.redoStack, state.document]
        : state.redoStack;

      if (state.document) {
        dispatch({
          type: 'SET_REDO_STACK',
          payload: newRedoStack
        });
      }
      dispatch({ type: 'SET_UNDO_STACK', payload: undoStackCopy });
      dispatch({ type: 'SET_DOCUMENT', payload: lastState });
      dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });
      documentRef.current = lastState;

      // Save to localStorage (always mark as dirty since undo is a change)
      if (state.session && state.documentResult) {
        saveTranscriptToLocalStorage(
          state.session.session_id,
          state.documentResult.result.id,
          {
            document: lastState,
            undoStack: undoStackCopy,
            redoStack: newRedoStack,
            isDirty: true
          }
        );
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }
  }, [
    state.undoStack,
    state.redoStack,
    state.document,
    state.session,
    state.documentResult,
    setSaveStatus,
    setLastLocalSave
  ]);

  const redo = useCallback(() => {
    if (state.redoStack.length === 0) return;

    const redoStackCopy = [...state.redoStack];
    const nextState = redoStackCopy.pop();

    if (nextState) {
      const newUndoStack = state.document
        ? [...state.undoStack, state.document]
        : state.undoStack;

      if (state.document) {
        dispatch({
          type: 'SET_UNDO_STACK',
          payload: newUndoStack
        });
      }
      dispatch({ type: 'SET_REDO_STACK', payload: redoStackCopy });
      dispatch({ type: 'SET_DOCUMENT', payload: nextState });
      dispatch({ type: 'INCREMENT_DOCUMENT_VERSION', payload: null });
      documentRef.current = nextState;

      // Save to localStorage (always mark as dirty since redo is a change)
      if (state.session && state.documentResult) {
        saveTranscriptToLocalStorage(
          state.session.session_id,
          state.documentResult.result.id,
          {
            document: nextState,
            undoStack: newUndoStack,
            redoStack: redoStackCopy,
            isDirty: true
          }
        );
        setSaveStatus('saved-locally');
        setLastLocalSave(Date.now());
      }
    }
  }, [
    state.redoStack,
    state.undoStack,
    state.document,
    state.session,
    state.documentResult,
    setSaveStatus,
    setLastLocalSave
  ]);

  // Global keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd (Mac) or Ctrl (Windows/Linux)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;

      // Undo: Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if (modifierKey && event.key === 'z' && !event.shiftKey) {
        if (state.undoStack.length > 0) {
          event.preventDefault();
          undo();
        }
      }

      // Redo: Cmd+Shift+Z (Mac) or Ctrl+Shift+Z (Windows/Linux)
      if (modifierKey && event.key === 'z' && event.shiftKey) {
        if (state.redoStack.length > 0) {
          event.preventDefault();
          redo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo, state.undoStack.length, state.redoStack.length]);

  const finishDocument = (
    sessionId: string,
    documentId: string,
    newStatus: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFinished: (status: boolean, error?: any) => void
  ) => {
    if (!state.document || !state.documentResult || !state.session) return;

    // Helper to update documentResult with new finished status
    const updateDocumentResultFinished = (finished: boolean) => {
      if (state.documentResult) {
        dispatch({
          type: 'SET_DOCUMENT_RESULT',
          payload: {
            ...state.documentResult,
            result: {
              ...state.documentResult.result,
              finished
            }
          }
        });
      }
    };

    if (!newStatus) {
      toggleStatus(
        { sessionId, documentId, status: newStatus },
        {
          onSuccess: () => {
            toast.success(
              t('status-updated-successfully', { status: 'false' })
            );
            updateDocumentResultFinished(newStatus);
            setFinished(newStatus);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            const errorMessage =
              error.response?.data?.error_description ||
              error.response?.data.detail ||
              error.response?.data.error ||
              error.response?.data.message ||
              t('failed-to-update-status');
            toast.error(errorMessage);
            setFinished(!newStatus, error);
          }
        }
      );
      return;
    }

    if (state.isDirty) {
      toast(t('document-has-unsaved-changes-saving-changes'));

      updateDocumentMutation(
        {
          sessionId: state.session.session_id,
          documentId: state.documentResult!.result.id!,
          document: state.document as DocumentObjectInnerDocument
        },
        {
          onSuccess: () => {
            toggleStatus(
              { sessionId, documentId, status: newStatus },
              {
                onSuccess: () => {
                  toast.success(
                    t('status-updated-successfully', { status: 'true' })
                  );
                  updateDocumentResultFinished(newStatus);
                  setFinished(newStatus);
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onError: (error: any) => {
                  const errorMessage =
                    error.response?.data?.error_description ||
                    error.response?.data.detail ||
                    error.response?.data.error ||
                    error.response?.data.message ||
                    t('failed-to-update-status');
                  toast.error(errorMessage);
                  setFinished(!newStatus, error);
                }
              }
            );
            setIsDirty(false);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            toast.error(t('failed-to-update-status'));
            setFinished(!newStatus, error);
          }
        }
      );
    } else {
      toggleStatus(
        { sessionId, documentId, status: newStatus },
        {
          onSuccess: () => {
            toast.success(t('status-updated-successfully', { status: 'true' }));
            updateDocumentResultFinished(newStatus);
            setFinished(newStatus);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            const errorMessage =
              error.response?.data?.error_description ||
              error.response?.data.detail ||
              error.response?.data.error ||
              error.response?.data.message ||
              t('failed-to-update-status');
            toast.error(errorMessage);
            setFinished(!newStatus, error);
          }
        }
      );
    }
  };

  const addSpeaker = (nextName: string, element: ScriptixDescendant) => {
    if (!state.document) return;

    const name = (nextName ?? '').trim();
    if (!name) return; // ignore empty/whitespace

    // Add to undo stack before making changes
    addVersionChange();

    // Update exactly ONE utterance — the first match for this element's
    // timings. Splitting a paragraph (whether via the Scissors button or an
    // Enter-key split) can leave sibling utterances sharing a start or stop
    // boundary, and renaming all of them would propagate across voices the
    // user did not touch. The clicked paragraph is first in document order
    // for its own `(start, stop)` tuple, so matching once is enough.
    let matched = false;
    const updated = state.document.document.map(utt => {
      if (matched) return utt;
      const sameUtterance =
        utt.start === element.start && utt.stop === element.stop;
      if (!sameUtterance) return utt;
      matched = true;
      return { ...utt, speaker: name };
    });

    const nextDoc: DocumentTranscriptV1 = {
      ...state.document,
      document: updated
    };

    // Update ref immediately to avoid race conditions
    documentRef.current = nextDoc;

    // Write the new document
    dispatch({ type: 'SET_DOCUMENT', payload: nextDoc });

    // Recompute speakers from the document (deduped + trimmed, no empties)
    const nextSpeakers = Array.from(
      new Set(
        updated.map(u => (u.speaker ?? '').trim()).filter(s => s.length > 0) // keep 'No name' if you treat it as a real value
      )
    );
    dispatch({ type: 'SET_SPEAKERS', payload: nextSpeakers });

    // Mark dirty - let debounced localStorage save handle persistence
    // DO NOT use silentSave here - it causes race conditions
    setIsDirty(true);
  };

  /** Name the utterances of ONE detected voice, located by the source
   *  diarization timeline rather than by their current display name.
   *
   *  `replaceSpeakers` above renames every utterance that currently shows a
   *  given name, which is what a manual rename means. A voice identity is
   *  different: two detected voices share a display name before anyone is
   *  named, and again as soon as the first person is named, so a name-matched
   *  sweep takes the other person's utterances with it — which is why naming
   *  the second speaker used to erase the first. The timeline is the same
   *  thing the backend keys the identity decision on, so these are exactly
   *  the utterances that decision covers. */
  const renameSpeakerByTimeline = (
    timeline: VoiceTimelineSpan[],
    speakerLabel: string,
    newSpeaker: string
  ) => {
    if (!state.document || !state.session || !state.documentResult) return;

    const updated = applyVoiceNameToUtterances(
      state.document.document,
      timeline,
      speakerLabel,
      newSpeaker
    );
    // Nothing of this voice is in the document, or it already carries the
    // name: leave the undo stack and the dirty flag alone.
    if (!updated) return;

    addVersionChange();

    const nextDoc: DocumentTranscriptV1 = {
      ...state.document,
      document: updated
    };

    documentRef.current = nextDoc;
    dispatch({ type: 'SET_DOCUMENT', payload: nextDoc });
    dispatch({ type: 'SET_SPEAKERS', payload: deriveSpeakers(nextDoc) });
    setIsDirty(true);
  };

  useEffect(() => {
    if (!state.document && transcriptDocument) {
      dispatch({
        type: 'SET_DOCUMENT',
        payload: transcriptDocument.result.document as DocumentTranscriptV1
      });
    }
    if (!state.documentResult && transcriptDocument) {
      dispatch({ type: 'SET_DOCUMENT_RESULT', payload: transcriptDocument });
    }
    if (
      (!state.speakers || state.speakers.length === 0) &&
      transcriptDocument
    ) {
      dispatch({
        type: 'SET_SPEAKERS',
        payload: (
          transcriptDocument.result.document as DocumentTranscriptV1
        ).document
          .filter(p => p.speaker && p.speaker.length > 0)
          .map(p => p.speaker || '')
      });
    }
  }, [transcriptDocument]);

  const deriveSpeakers = (doc?: DocumentTranscriptV1) => {
    if (!doc) return [];
    const set = new Set(
      doc.document.map(u => (u.speaker ?? '').trim()).filter(s => s.length > 0)
    );
    return Array.from(set);
  };

  useEffect(() => {
    if (state.document) {
      dispatch({
        type: 'SET_SPEAKERS',
        payload: deriveSpeakers(state.document)
      });
    }
  }, [state.document]);

  const replaceSpeakers = (oldSpeaker: string, newSpeaker: string) => {
    if (!state.document || !state.session || !state.documentResult) return;

    // Add to undo stack before making changes
    addVersionChange();

    const updated = state.document.document.map(utt =>
      utt.speaker === oldSpeaker ? { ...utt, speaker: newSpeaker } : utt
    );

    const nextDoc: DocumentTranscriptV1 = {
      ...state.document,
      document: updated
    };

    // Update ref immediately to avoid race conditions
    documentRef.current = nextDoc;

    dispatch({ type: 'SET_DOCUMENT', payload: nextDoc });

    dispatch({ type: 'SET_SPEAKERS', payload: deriveSpeakers(nextDoc) });

    // Mark dirty - let debounced localStorage save handle persistence
    // DO NOT use silentSave here - it causes race conditions
    setIsDirty(true);
  };

  // Track last edit time to batch rapid consecutive edits
  const lastEditTimeRef = useRef<number>(0);
  const pendingEditTimerRef = useRef<NodeJS.Timeout | null>(null);

  const editorUpdateParagraph = (children: Descendant[]) => {
    // Read from `documentRef.current` instead of `state.document`:
    // Replace All iterates every mounted editor synchronously and
    // each editor's Slate `onChange` lands here. React batches those
    // dispatches, so each call sees the same STALE `state.document`
    // from closure and only the last dispatch wins — paragraphs
    // 1..N-1 silently lose their replacement, and the server PUT
    // (also sourced from state.document) misses them too. The ref is
    // mutated synchronously below, so each follower reads the
    // previous call's result and compounds correctly.
    const sourceDoc = documentRef.current ?? state.document;
    if (!sourceDoc || !state.session) {
      return;
    }

    if (!children || children.length === 0) {
      return;
    }

    const childrenDocument = (
      children.filter(
        (child: Descendant) => (child as ScriptixDescendant).type !== 'speaker'
      ) as Array<DocumentTranscriptUtteranceV1>
    )?.[0];

    if (!childrenDocument) return;

    const paragraph = sourceDoc.document.find(
      p =>
        p.start === childrenDocument.start && p.stop === childrenDocument.stop
    );

    if (!paragraph) return;

    const index = sourceDoc.document.indexOf(paragraph);

    if (index === -1) return;

    // Quick shallow check first before expensive deep equality
    const childrenChanged =
      paragraph.children.length !== childrenDocument.children.length;

    // Only do deep equality check if shallow check suggests a change or lengths match
    const hasChanged =
      childrenChanged ||
      !transcriptParagraphsEqual(paragraph.children, childrenDocument.children);
    if (!hasChanged) return;

    const now = Date.now();
    const timeSinceLastEdit = now - lastEditTimeRef.current;

    // Batch undo history: only add to undo stack if more than 500ms since last edit
    // This prevents creating an undo entry for every single keystroke
    if (timeSinceLastEdit > 500) {
      addVersionChange();
    }

    lastEditTimeRef.current = now;

    const updatedDocument = [...sourceDoc.document];
    updatedDocument[index] = {
      ...paragraph,
      children: childrenDocument.children
    };

    const newDoc = {
      ...sourceDoc,
      document: updatedDocument
    };

    // Update ref immediately to avoid race conditions
    documentRef.current = newDoc;

    dispatch({
      type: 'SET_DOCUMENT',
      payload: newDoc
    });

    setIsDirty(true);

    // Clear any pending edit timer
    if (pendingEditTimerRef.current) {
      clearTimeout(pendingEditTimerRef.current);
    }

    // Add to undo stack after 500ms of inactivity (end of editing burst)
    pendingEditTimerRef.current = setTimeout(() => {
      if (timeSinceLastEdit <= 500 && state.document) {
        // This was part of a rapid editing session, add final state to undo
        const nextUndo = [...state.undoStack, state.document];
        const trimmedUndo =
          nextUndo.length > MAX_HISTORY
            ? nextUndo.slice(nextUndo.length - MAX_HISTORY)
            : nextUndo;

        dispatch({
          type: 'SET_UNDO_STACK',
          payload: trimmedUndo
        });
        dispatch({
          type: 'SET_REDO_STACK',
          payload: []
        });
      }
    }, 500);
  };

  const setIsDirty = (isDirty: boolean) => {
    dispatch({ type: 'SET_IS_DIRTY', payload: isDirty });
  };

  const setMatches = (m: DocumentTranscriptTextV1[]) => {
    dispatch({ type: 'SET_MATCHES', payload: m || [] });
  };

  const focusMatch = (m: DocumentTranscriptTextV1 | null) => {
    dispatch({ type: 'SET_CURRENT_MATCH', payload: m ?? null });

    if (m && m.start !== undefined && m.stop !== undefined) {
      window.dispatchEvent(
        new CustomEvent('transcript:focusMatch', {
          detail: { start: m.start, stop: m.stop }
        })
      );
    }
  };

  const setSelectedParagraphs = (indices: number[]) => {
    dispatch({ type: 'SET_SELECTED_PARAGRAPHS', payload: indices });
  };

  const toggleParagraphSelection = (index: number, multiSelect: boolean) => {
    if (!multiSelect) {
      // Single selection mode - just select this one
      dispatch({ type: 'SET_SELECTED_PARAGRAPHS', payload: [index] });
    } else {
      // Multi-select mode
      const isSelected = state.selectedParagraphs.includes(index);
      if (isSelected) {
        // Deselect
        dispatch({
          type: 'SET_SELECTED_PARAGRAPHS',
          payload: state.selectedParagraphs.filter(i => i !== index)
        });
      } else {
        // Add to selection
        dispatch({
          type: 'SET_SELECTED_PARAGRAPHS',
          payload: [...state.selectedParagraphs, index].sort((a, b) => a - b)
        });
      }
    }
  };

  const clearParagraphSelection = () => {
    dispatch({ type: 'SET_SELECTED_PARAGRAPHS', payload: [] });
  };

  const mergeUtterances = (indices: number[]) => {
    if (!state.document || indices.length < 2) return;

    // Sort indices to ensure we merge in order
    const sortedIndices = [...indices].sort((a, b) => a - b);

    // Add to undo stack before making changes
    addVersionChange();

    const updatedDocument = [...state.document.document];

    // Get all utterances to merge
    const utterancesToMerge = sortedIndices.map(i => updatedDocument[i]);

    // Create merged utterance
    // Use the first utterance as the base
    const firstUtterance = utterancesToMerge[0];
    const lastUtterance = utterancesToMerge[utterancesToMerge.length - 1];

    // Combine all children from all utterances
    const mergedChildren: DocumentTranscriptParagraphV1[] = [];
    for (const utterance of utterancesToMerge) {
      mergedChildren.push(...utterance.children);
    }

    const mergedUtterance: DocumentTranscriptUtteranceV1 = {
      type: 'utterance',
      speaker: firstUtterance.speaker,
      start: firstUtterance.start,
      stop: lastUtterance.stop,
      children: mergedChildren
    };

    // Remove the utterances we're merging (in reverse order to maintain indices)
    for (let i = sortedIndices.length - 1; i >= 0; i--) {
      updatedDocument.splice(sortedIndices[i], 1);
    }

    // Insert merged utterance at the position of the first selected utterance
    updatedDocument.splice(sortedIndices[0], 0, mergedUtterance);

    const newDoc = {
      ...state.document,
      document: updatedDocument
    };

    // Update ref immediately to avoid race conditions
    documentRef.current = newDoc;

    dispatch({
      type: 'SET_DOCUMENT',
      payload: newDoc
    });

    // Clear selection after merge
    dispatch({ type: 'SET_SELECTED_PARAGRAPHS', payload: [] });

    setIsDirty(true);

    // Show success feedback
    toast.success(t('paragraphs-merged-successfully'));
  };

  // --- Conflict resolution (DraftConflictModal) -----------------------------
  // Apply the local draft over the server baseline and continue editing it.
  const resolveConflictKeepLocal = useCallback(() => {
    if (!conflict) return;
    dispatch({ type: 'SET_DOCUMENT', payload: conflict.localDocument });
    dispatch({ type: 'SET_UNDO_STACK', payload: conflict.localUndo });
    dispatch({ type: 'SET_REDO_STACK', payload: conflict.localRedo });
    documentRef.current = conflict.localDocument;
    undoStackRef.current = conflict.localUndo;
    redoStackRef.current = conflict.localRedo;
    setSaveStatus('saved-locally');
    setLastLocalSave(conflict.localTimestamp || Date.now());
    setConflict(null);
  }, [conflict, setSaveStatus, setLastLocalSave]);

  // Discard the local draft and keep the server copy (already the live state).
  const resolveConflictLoadCloud = useCallback(() => {
    if (state.session && state.documentResult) {
      clearTranscriptFromLocalStorage(
        state.session.session_id,
        state.documentResult.result.id
      );
    }
    setSaveStatus('synced');
    setConflict(null);
  }, [state.session, state.documentResult, setSaveStatus]);

  return {
    contextValue: {
      ...state,
      reset,
      setSearch,
      setStartStopWord,
      setPopoverOpen,
      setPopoverPosition,
      setShowInfo,
      setClickedComment,
      setShowJumpButton,
      searchInputRef,
      saveDocument,
      saveDocumentLoading,
      documentResetting,
      finishedLoading,
      handleMediaElementReady,
      applyStyle,
      setCaseSensitive,
      setWholeWord,
      setCurrentMatch,
      addSpeaker,
      setEditor,
      updateDocument,
      autoSave: silentSave,
      finishDocument,
      addUtterance,
      deleteUtterance,
      currentEditorRef,
      splitUtterance,
      replaceSpeakers,
      renameSpeakerByTimeline,
      editorUpdateParagraph,
      matches: state.matches,
      setMatches,
      focusMatch,
      undo,
      redo,
      updateDocumentResult,
      reloadFromServer,
      selectedParagraphs: state.selectedParagraphs,
      setSelectedParagraphs,
      toggleParagraphSelection,
      clearParagraphSelection,
      mergeUtterances
    },
    conflict,
    resolveConflictKeepLocal,
    resolveConflictLoadCloud
  };
}
